import React, { Suspense, useCallback, useContext, useEffect } from 'react';
import cx from 'classnames';
import { getScreenFromUrl } from '@trello/atlassian-analytics';
import { forNamespace } from '@trello/i18n';
import { Key, Scope, useShortcut } from '@trello/keybindings';
import { Button, ButtonProps } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
import { FilterIcon } from '@trello/nachos/icons/filter';
import { Popover, usePopover, PopoverPlacement } from '@trello/nachos/popover';
import { useRecentlyUsedFeaturePreloader } from '@trello/recently-used-feature-preloader';
import { useSharedState } from '@trello/shared-state';
import { FilterPopoverTestIds } from '@trello/test-ids';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { ChunkLoadErrorBoundary } from 'app/src/components/ChunkLoadErrorBoundary';
import { ViewFiltersContext } from 'app/src/components/ViewFilters';
import type { CountableViewFilterParams } from 'app/src/components/ViewFilters';
import { FilterPopoverSkeleton } from './FilterPopoverSections/FilterPopoverSkeleton';
import { filteredCardsCountState } from './filteredCardsCountState';
import { useFilterPopoverAnalytics } from './useFilterPopoverAnalytics';
import styles from './FilterPopoverButton.less';

const format = forNamespace('filter popover');

// Filters to skip when calculating active filters and clearing filters.
export const SKIPPED_FILTERS: CountableViewFilterParams = ['boards', 'sort'];

export interface FilterPopoverButtonProps {
  /**
   * State styling is manually applied within this component, as it's considered
   * "active" when any filters are applied. Appearance is referenced directly in
   * our LESS styles as a result.
   * @default 'default'
   */
  appearance?: Extract<
    ButtonProps['appearance'],
    'default' | 'transparent' | 'transparent-dark'
  >;
  /**
   * Supplemental classname applied to the button group. Use this for spacing
   * the FilterPopoverButton in context, not for styling internals if possible.
   */
  className?: string;
  isDisabled?: boolean;
  onLoad?: () => void;
}

export const FilterPopoverButton: React.FunctionComponent<FilterPopoverButtonProps> = ({
  appearance = 'default',
  className,
  isDisabled,
  onLoad,
}) => {
  const Analytics = useFilterPopoverAnalytics();
  const { viewFilters } = useContext(ViewFiltersContext);

  const [FilterPopover, trackFeatureUsage] = useRecentlyUsedFeaturePreloader({
    featureName: 'FilterPopover',
    useLazyComponentArgs: [
      () => import(/* webpackChunkName: "filter-popover" */ './FilterPopover'),
      { namedImport: 'FilterPopover' },
    ],
    onPreloadedCallback: onLoad,
  });

  const popoverResult = usePopover<HTMLButtonElement>({
    size: 'large',
    onShow: () => {
      Analytics.sendScreenEvent({
        name: 'filterPopoverInlineDialog',
        attributes: viewFilters.getCommonAttributes?.(),
      });
    },
    onHide: () => {
      Analytics.sendClosedComponentEvent({
        componentType: 'inlineDialog',
        componentName: 'filterPopoverInlineDialog',
        source: getScreenFromUrl(),
        attributes: viewFilters.getCommonAttributes?.(),
      });
    },
    placement: PopoverPlacement.BOTTOM_END,
  });

  useEffect(() => {
    viewFilters.setFilterPopoverResult?.(popoverResult);

    return () => {
      viewFilters.setFilterPopoverResult?.(null);
    };
    // eslint-disable-next-line @trello/exhaustive-deps
  }, []);

  const { toggle, triggerRef, popoverProps } = popoverResult;

  const togglePopover = useCallback(() => {
    onLoad?.();
    trackFeatureUsage();
    // We only want open clicks; close clicks aren't very interesting.
    if (!popoverProps.isVisible) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'filterPopoverButton',
        source: getScreenFromUrl(),
        attributes: viewFilters.getCommonAttributes?.(),
      });
    }
    toggle();
  }, [
    Analytics,
    onLoad,
    popoverProps.isVisible,
    toggle,
    trackFeatureUsage,
    viewFilters,
  ]);

  useShortcut(togglePopover, { scope: Scope.BoardView, key: Key.f });

  const onClickClearFiltersButton = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'clearFiltersButton',
      source: getScreenFromUrl(),
      attributes: viewFilters.getCommonAttributes?.(),
    });

    viewFilters.resetFilters(SKIPPED_FILTERS);
  }, [Analytics, viewFilters]);

  const isSavedView = viewFilters.contextType === 'savedView';
  const isFiltering = viewFilters.filters.isFiltering(
    viewFilters.contextType === 'singleBoard'
      ? SKIPPED_FILTERS
      : [
          ...SKIPPED_FILTERS,
          'title',
        ] /* skipping the 'title' filter for workspace views */,
  );
  const isPreviewFiltering = isSavedView && viewFilters.isFiltersPreviewActive;
  const isActive =
    popoverProps.isVisible || (isSavedView ? isPreviewFiltering : isFiltering);
  const showCardCountAndXButton = isSavedView
    ? isPreviewFiltering
    : isFiltering;

  const [cardsCountState] = useSharedState(filteredCardsCountState);

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-panorama',
        feature: 'Project Brita',
      }}
    >
      <div className={cx(styles.buttonGroup)} data-appearance={appearance}>
        <Button
          appearance={appearance}
          className={cx(
            styles.filterButton,
            styles[appearance],
            isActive && styles.isActive,
            isFiltering && styles.isFiltering,
          )}
          iconBefore={<FilterIcon size="small" />}
          isDisabled={isDisabled}
          onClick={togglePopover}
          ref={triggerRef}
          data-test-id={FilterPopoverTestIds.FilterPopoverButton}
        >
          {format('filter')}
          {showCardCountAndXButton && (
            <span
              data-test-id={FilterPopoverTestIds.FilterPopoverButtonCardCount}
              className={cx(
                styles.cardsCount,
                cardsCountState.isLoading && styles.isLoadingCards,
              )}
            >
              {cardsCountState.count}
            </span>
          )}
        </Button>
        {showCardCountAndXButton ? (
          <Button
            data-test-id={FilterPopoverTestIds.FilterPopoverButtonX}
            appearance={appearance}
            className={cx(styles.clearFiltersButton, styles[appearance])}
            iconBefore={<CloseIcon size="small" />}
            isDisabled={isDisabled}
            onClick={onClickClearFiltersButton}
          />
        ) : null}
      </div>
      {popoverProps.isVisible && (
        <Popover
          title={format('filter')}
          dontOverlapAnchorElement
          {...popoverProps}
          data-test-id={FilterPopoverTestIds.FilterPopoverContents}
        >
          <Suspense fallback={<FilterPopoverSkeleton />}>
            <ChunkLoadErrorBoundary
              fallback={<FilterPopoverSkeleton />}
              retryAfter={15000}
            >
              <FilterPopover viewFilters={viewFilters} />
            </ChunkLoadErrorBoundary>
          </Suspense>
        </Popover>
      )}
    </ErrorBoundary>
  );
};
